import { useEffect, useState } from "react";
import {
  Award, Calendar, CalendarCheck, CalendarDays, CalendarHeart, Crown,
  Diamond, DollarSign, Flame, Medal, Rocket, Sparkles, Star, Swords,
  TrendingUp, Trophy, Zap,
} from "lucide-react";
import { profileService } from "@/services";

// Lucide icons referenced by name in the backend badge catalog.
// Adding a new icon: import it here and add it to the map.
// Award is kept as the fallback default even though no badge uses it.
const ICON_MAP = {
  Award, Calendar, CalendarCheck, CalendarDays, CalendarHeart, Crown,
  Diamond, DollarSign, Flame, Medal, Rocket, Sparkles, Star, Swords,
  TrendingUp, Trophy, Zap,
};

const TIER_STYLES = {
  bronze: {
    earned: "bg-gradient-to-br from-orange-200 to-orange-300 dark:from-orange-900/40 dark:to-orange-800/40 border-orange-400/40 text-orange-800 dark:text-orange-200",
    icon: "text-orange-600 dark:text-orange-300",
  },
  silver: {
    earned: "bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700/40 dark:to-slate-600/40 border-slate-400/40 text-slate-700 dark:text-slate-200",
    icon: "text-slate-500 dark:text-slate-300",
  },
  gold: {
    earned: "bg-gradient-to-br from-amber-200 to-yellow-300 dark:from-amber-900/40 dark:to-yellow-800/40 border-amber-400/50 text-amber-900 dark:text-amber-200",
    icon: "text-amber-500 dark:text-amber-300",
  },
};

function BadgeTile({ badge, earned }) {
  const Icon = ICON_MAP[badge.icon] || Award;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
  const earnedClass = earned
    ? `${tier.earned} border-2 shadow-sm`
    : "bg-white/40 dark:bg-slate-900/40 border border-dashed border-[#1A237E]/15 dark:border-[#E3F2FD]/15 opacity-50";
  return (
    <div
      className={`rounded-2xl p-3 flex flex-col items-center text-center ${earnedClass}`}
      title={`${badge.label}\n${badge.description}`}
      data-testid={`badge-${badge.id}${earned ? "" : "-locked"}`}
    >
      <Icon className={`w-6 h-6 mb-1 ${earned ? tier.icon : "text-[#1A237E]/40 dark:text-[#E3F2FD]/40"}`} />
      <p className={`text-[11px] font-bold leading-tight ${earned ? "" : "text-[#1A237E]/50 dark:text-[#E3F2FD]/50"}`}>
        {badge.label}
      </p>
    </div>
  );
}

// Renders all defined badges in tier order, earned ones in their
// tier color, unearned ones as faded silhouettes. Caller passes in
// the user's earned badges (already-derived array from the profile
// response). Catalog is fetched once on mount and cached on the
// component instance.
export default function BadgesGrid({ earnedBadges = [] }) {
  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    let cancelled = false;
    profileService
      .getBadgeCatalog()
      .then((data) => !cancelled && setCatalog(data?.items || []))
      .catch(() => !cancelled && setCatalog([]));
    return () => {
      cancelled = true;
    };
  }, []);

  if (catalog === null) return null; // skeleton: no flicker before first paint

  const earnedSet = new Set(earnedBadges.map((b) => b.id));
  const tierOrder = { gold: 0, silver: 1, bronze: 2 };
  const sorted = [...catalog].sort((a, b) => {
    const aEarned = earnedSet.has(a.id) ? 0 : 1;
    const bEarned = earnedSet.has(b.id) ? 0 : 1;
    if (aEarned !== bEarned) return aEarned - bEarned;
    return (tierOrder[a.tier] ?? 99) - (tierOrder[b.tier] ?? 99);
  });

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="badges-grid">
      {sorted.map((b) => (
        <BadgeTile key={b.id} badge={b} earned={earnedSet.has(b.id)} />
      ))}
    </div>
  );
}
